export interface LanguageDetails {
  nameEnglish: string;
  code: LanguageCode;
  countries: Country[];
  isRightToLeft?: boolean;
  translations: { [key: string]: string };
}

export type LanguageCode = string;

export type DefaultLanguageCode = LanguageCode;

export const DefaultLanguageCode: DefaultLanguageCode = 'Default';

export type Country = string;

export type LanguagesConfigurations = Map<LanguageCode, LanguageConfiguration>;

export interface LanguageConfiguration {
  nameEnglish: string;
  countries: Country[];
  translations: { [key: string]: string };
}

export const TRANSLATION_KEYS = ['submit', 'continue', 'specify', 'addAnotherOpenEnded', 'removeOpenEndedRow', 'deselectAll', 'openAnswerError'] as const;

export const defaultLanguage: LanguageDetails = {
  nameEnglish: 'Default',
  code: 'Default',
  countries: [],
  translations: {
    submit: 'Submit',
    continue: 'Continue',
    specify: 'Please specify',
    addAnotherOpenEnded: 'Add another',
    removeOpenEndedRow: 'Remove answer',
    deselectAll: 'Deselect all',

    openAnswerError: 'You can’t save an empty answer option. Please specify the answer or uncheck the option.'
  }
};

export const arabicLanguageDetails: LanguageDetails = {
  nameEnglish: 'Arabic',
  code: 'ar',
  countries: ['Egypt', 'Israel', 'Morocco', 'Saudi Arabia', 'UAE'],
  translations: {
    submit: 'إرسال',
    continue: 'متابعة',
    specify: 'من فضلك حدد',
    addAnotherOpenEnded: 'إضافة إجابة',
    removeOpenEndedRow: 'إزالة الإجابة',
    deselectAll: 'إلغاء تحديد الكل',

    openAnswerError: 'يمكنك حفظ خيار إجابة فارغ. يرجى تحديد الإجابة أو إلغاء تحديد الخيار.'
  },
  isRightToLeft: true
}; // Used in testing

export const languageData: LanguageDetails[] = [
  defaultLanguage,
  arabicLanguageDetails,
  {
    nameEnglish: 'Flemish',
    code: 'nl-BE',
    countries: ['Belgium'],
    translations: {
      submit: 'indienen',
      continue: 'Doorgaan',
      specify: 'Gelieve te specificeren',
      addAnotherOpenEnded: 'Nog een toevoegen',
      removeOpenEndedRow: 'Antwoord verwijderen',
      deselectAll: 'Alles deselecteren',

      openAnswerError: 'Je kunt een leeg antwoordoptie niet opslaan. Gelieve het antwoord te specificeren of de optie uit te vinken.'
    }
  },
  {
    nameEnglish: 'Belgian French',
    code: 'fr-BE',
    countries: ['Belgium'],
    translations: {
      submit: 'Envoyer',
      continue: 'Continuer',
      specify: 'Veuillez spécifier',
      addAnotherOpenEnded: 'Ajouter une autre',
      removeOpenEndedRow: 'Supprimer la réponse',
      deselectAll: 'Tout désélectionner',

      openAnswerError: `Vous ne pouvez pas enregistrer une option de réponse vide. Veuillez spécifier la réponse ou décocher l'option.`
    }
  },
  {
    nameEnglish: 'Canadian French',
    code: 'fr-CA',
    countries: ['Canada'],
    translations: {
      submit: 'Envoyer',
      continue: 'Continuer',
      specify: 'Veuillez spécifier',
      addAnotherOpenEnded: 'Ajouter une autre',
      removeOpenEndedRow: 'Supprimer la réponse',
      deselectAll: 'Tout désélectionner',

      openAnswerError: `Vous ne pouvez pas sauvegarder une option de réponse vide. Veuillez spécifier la réponse ou décocher l'option.`
    }
  },
  {
    nameEnglish: 'Danish',
    code: 'da',
    countries: ['Denmark'],
    translations: {
      submit: 'indsend',
      continue: 'Fortsæt',
      specify: 'Venligst specificer',
      addAnotherOpenEnded: 'Tilføj endnu en',
      removeOpenEndedRow: 'Fjern svar',
      deselectAll: 'Fravælg alle',

      openAnswerError: 'Du kan ikke gemme en tom svarmulighed. Venligst specificer svaret eller fjern markeringen af muligheden.'
    }
  },
  {
    nameEnglish: 'German',
    code: 'de',
    countries: ['Austria', 'Germany', 'Switzerland'],
    translations: {
      submit: 'Absenden',
      continue: 'Weiter',
      specify: 'Bitte angeben',
      addAnotherOpenEnded: 'Weitere hinzufügen',
      removeOpenEndedRow: 'Antwort entfernen',
      deselectAll: 'Auswahl aufheben',

      openAnswerError: 'Sie können keine leere Antwortoption speichern. Bitte geben Sie die Antwort an oder deaktivieren Sie die Option.'
    }
  },
  {
    nameEnglish: 'English - US',
    code: 'en',
    countries: [
      'Australia',
      'Canada',
      'Egypt',
      'Ghana',
      'Hong Kong',
      'Ireland',
      'Kenya',
      'Malaysia',
      'New Zealand',
      'Nigeria',
      'Philippines',
      'Saudi Arabia',
      'Singapore',
      'South Africa',
      'UAE',
      'UK',
      'USA'
    ],
    translations: {
      submit: 'Submit',
      continue: 'Continue',
      specify: 'Please specify',
      addAnotherOpenEnded: 'Add another',
      removeOpenEndedRow: 'Remove answer',
      deselectAll: 'Deselect all',

      openAnswerError: 'You can’t save an empty answer option. Please specify the answer or uncheck the option.'
    }
  },
  {
    nameEnglish: 'Latin American Spanish',
    code: 'es-419',
    countries: ['Colombia', 'Mexico', 'USA'],
    translations: {
      submit: 'Enviar',
      continue: 'Continuar',
      specify: 'Por favor especifique',
      addAnotherOpenEnded: 'Añadir otra',
      removeOpenEndedRow: 'Eliminar respuesta',
      deselectAll: 'Deseleccionar todo',

      openAnswerError: 'No puede guardar una opción de respuesta vacía. Por favor especifique la respuesta o desmarque la opción.'
    }
  },
  {
    nameEnglish: 'Spanish',
    code: 'es',
    countries: ['Spain'],
    translations: {
      submit: 'Enviar',
      continue: 'Continuar',
      specify: 'Por favor especifique',
      addAnotherOpenEnded: 'Añadir otra',
      removeOpenEndedRow: 'Eliminar respuesta',
      deselectAll: 'Deseleccionar todo',

      openAnswerError: 'No puede guardar una opción de respuesta vacía. Por favor, especifique la respuesta o desmarque la opción.'
    }
  },
  {
    nameEnglish: 'French',
    code: 'fr',
    countries: ['France', 'Morocco', 'Switzerland'],
    translations: {
      submit: 'Envoyer',
      continue: 'Continuer',
      specify: 'Veuillez spécifier',
      addAnotherOpenEnded: 'Ajouter une autre',
      removeOpenEndedRow: 'Supprimer la réponse',
      deselectAll: 'Tout désélectionner',

      openAnswerError: `Vous ne pouvez pas sauvegarder une option de réponse vide. Veuillez spécifier la réponse ou décocher l'option.`
    }
  },
  {
    nameEnglish: 'Hebrew',
    code: 'he',
    countries: ['Israel'],
    translations: {
      submit: 'הגש',
      continue: 'המשך',
      specify: 'אנא ציין',
      addAnotherOpenEnded: 'הוסף עוד',
      removeOpenEndedRow: 'הסר תשובה',
      deselectAll: 'בטל את כל הבחירות',

      openAnswerError: 'אינך יכול לשמור אפשרות תשובה ריקה. אנא ציין תשובה או ביטל את בחירת האפשרות.'
    },
    isRightToLeft: true
  },
  {
    nameEnglish: 'Hindi',
    code: 'hi',
    countries: ['India'],
    translations: {
      submit: 'सबमिट करें',
      continue: 'जारी रखें',
      specify: 'कृपया निर्दिष्ट करें',
      addAnotherOpenEnded: 'और जोड़ें',
      removeOpenEndedRow: 'उत्तर हटाएं',
      deselectAll: 'सभी का चयन हटाएँ',

      openAnswerError: 'आप एक खाली उत्तर विकल्प नहीं सहेज सकते। कृपया उत्तर निर्दिष्ट करें या विकल्प को अनचेक करें।'
    }
  },
  {
    nameEnglish: 'Indonesian',
    code: 'id',
    countries: ['Indonesia'],
    translations: {
      submit: 'Kirim',
      continue: 'Lanjutkan',
      specify: 'Harap tentukan',
      addAnotherOpenEnded: 'Tambah lagi',
      removeOpenEndedRow: 'Hapus jawaban',
      deselectAll: 'Batalkan semua pilihan',

      openAnswerError: 'Anda tidak dapat menyimpan opsi jawaban kosong. Harap tentukan jawaban atau hilangkan centang opsi tersebut.'
    }
  },
  {
    nameEnglish: 'Italian',
    code: 'it',
    countries: ['Italy', 'Switzerland'],
    translations: {
      submit: 'Invia',
      continue: 'Continua',
      specify: 'Si prega di specificare',
      addAnotherOpenEnded: 'Aggiungi un’altra',
      removeOpenEndedRow: 'Rimuovi risposta',
      deselectAll: 'Deselezionare tutto',

      openAnswerError: `Non puoi salvare un'opzione di risposta vuota. Si prega di specificare la risposta o deselezionare l'opzione.`
    }
  },
  {
    nameEnglish: 'Japanese',
    code: 'ja',
    countries: ['Japan'],
    translations: {
      submit: '送信',
      continue: '続ける',
      specify: '詳細を指定してください',
      addAnotherOpenEnded: '別の回答を追加',
      removeOpenEndedRow: '回答を削除',
      deselectAll: 'すべて選択解除',

      openAnswerError: '空の回答オプションを保存することはできません。回答を指定するか、オプションのチェックを外してください。'
    }
  },
  {
    nameEnglish: 'Korean',
    code: 'ko',
    countries: ['South Korea'],
    translations: {
      submit: '제출',
      continue: '계속',
      specify: '명시해 주세요',
      addAnotherOpenEnded: '답변 추가',
      removeOpenEndedRow: '답변 삭제',
      deselectAll: '모두 선택 해제',

      openAnswerError: '빈 답변 옵션을 저장할 수 없습니다. 답변을 명시하거나 옵션의 체크를 취소해 주세요.'
    }
  },
  {
    nameEnglish: 'Malay',
    code: 'ms',
    countries: ['Malaysia'],
    translations: {
      submit: 'Hantar',
      continue: 'Teruskan',
      specify: 'Sila nyatakan',
      addAnotherOpenEnded: 'Tambah lagi',
      removeOpenEndedRow: 'Buang jawapan',
      deselectAll: 'Nyahpilih semua',

      openAnswerError: 'Anda tidak boleh menyimpan pilihan jawapan yang kosong. Sila nyatakan jawapan atau kosongkan pilihan tersebut.'
    }
  },
  {
    nameEnglish: 'Dutch',
    code: 'nl',
    countries: ['Netherlands'],
    translations: {
      submit: 'Verzenden',
      continue: 'Doorgaan',
      specify: 'Gelieve te specificeren',
      addAnotherOpenEnded: 'Nog een toevoegen',
      removeOpenEndedRow: 'Antwoord verwijderen',
      deselectAll: 'Alles deselecteren',

      openAnswerError: 'Je kunt een leeg antwoordoptie niet opslaan. Gelieve het antwoord te specificeren of de optie uit te vinken.'
    }
  },
  {
    nameEnglish: 'Polish',
    code: 'pl',
    countries: ['Poland'],
    translations: {
      submit: 'Wyślij',
      continue: 'Kontynuuj',
      specify: 'Proszę określić',
      addAnotherOpenEnded: 'Dodaj kolejną',
      removeOpenEndedRow: 'Usuń odpowiedź',
      deselectAll: 'Odznacz wszystko',

      openAnswerError: 'Nie możesz zapisać pustej opcji odpowiedzi. Proszę określić odpowiedź lub odznaczyć opcję.'
    }
  },
  {
    nameEnglish: 'Portuguese',
    code: 'pt',
    countries: ['Portugal'],
    translations: {
      submit: 'Enviar',
      continue: 'Continuar',
      specify: 'Por favor especifique',
      addAnotherOpenEnded: 'Adicionar outra',
      removeOpenEndedRow: 'Remover resposta',
      deselectAll: 'Desmarcar tudo',

      openAnswerError: 'Não é possível salvar uma opção de resposta vazia. Por favor, especifique a resposta ou desmarque a opção.'
    }
  },
  {
    nameEnglish: 'Brazilian Portuguese',
    code: 'pt-BR',
    countries: ['Brazil'],
    translations: {
      submit: 'Enviar',
      continue: 'Continuar',
      specify: 'Por favor, especifique',
      addAnotherOpenEnded: 'Adicionar outra',
      removeOpenEndedRow: 'Remover resposta',
      deselectAll: 'Desmarcar tudo',

      openAnswerError: 'Não é possível salvar uma opção de resposta vazia. Por favor, especifique a resposta ou desmarque a opção.'
    }
  },
  {
    nameEnglish: 'Romanian',
    code: 'ro',
    countries: ['Romania'],
    translations: {
      submit: 'Trimiteți',
      continue: 'Continuă',
      specify: 'Vă rugăm să specificați',
      addAnotherOpenEnded: 'Adăugați încă una',
      removeOpenEndedRow: 'Eliminați răspunsul',
      deselectAll: 'Deselectați tot',

      openAnswerError: 'Nu puteți salva o opțiune de răspuns goală. Vă rugăm să specificați răspunsul sau să debifați opțiunea.'
    }
  },
  {
    nameEnglish: 'Russian',
    code: 'ru',
    countries: ['Russia'],
    translations: {
      submit: 'Отправить',
      continue: 'Продолжить',
      specify: 'Пожалуйста, укажите',
      addAnotherOpenEnded: 'Добавить ещё',
      removeOpenEndedRow: 'Удалить ответ',
      deselectAll: 'Очистить выбор',

      openAnswerError: 'Невозможно сохранить пустой вариант ответа. Пожалуйста, укажите ответ или снимите флажок с опции.'
    }
  },
  {
    nameEnglish: 'Swedish',
    code: 'sv',
    countries: ['Sweden'],
    translations: {
      submit: 'Skicka',
      continue: 'Fortsätt',
      specify: 'Vänligen specificera',
      addAnotherOpenEnded: 'Lägg till fler',
      removeOpenEndedRow: 'Ta bort svar',
      deselectAll: 'Avmarkera alla',

      openAnswerError: 'Du kan inte spara ett tomt svarsalternativ. Vänligen specificera svaret eller avmarkera alternativet.'
    }
  },
  {
    nameEnglish: 'Tagalog',
    code: 'tl',
    countries: ['Philippines'],
    translations: {
      submit: 'I-submit',
      continue: 'Magpatuloy',
      specify: 'Mangyaring tukuyin',
      addAnotherOpenEnded: 'Magdagdag pa',
      removeOpenEndedRow: 'Alisin ang sagot',
      deselectAll: 'Alisin ang pagkakapili sa lahat',

      openAnswerError: 'Hindi mo maaaring i-save ang isang walang sagot na opsyon. Mangyaring tukuyin ang sagot o i-uncheck ang opsyon.'
    }
  },
  {
    nameEnglish: 'Thai',
    code: 'th',
    countries: ['Thailand'],
    translations: {
      submit: 'ส่ง',
      continue: 'ดำเนินการต่อ',
      specify: 'กรุณาระบุ',
      addAnotherOpenEnded: 'เพิ่มอีก',
      removeOpenEndedRow: 'ลบคำตอบ',
      deselectAll: 'ยกเลิกการเลือกทั้งหมด',

      openAnswerError: 'คุณไม่สามารถบันทึกตัวเลือกคำตอบที่ว่างเปล่าได้ กรุณาระบุคำตอบหรือยกเลิกการเลือกตัวเลือก'
    }
  },
  {
    nameEnglish: 'Turkish',
    code: 'tr',
    countries: ['Turkey'],
    translations: {
      submit: 'Gönder',
      continue: 'Devam',
      specify: 'Lütfen belirtin',
      addAnotherOpenEnded: 'Bir tane daha ekle',
      removeOpenEndedRow: 'Yanıtı kaldır',
      deselectAll: 'Tüm seçimleri kaldır',

      openAnswerError: 'Boş bir cevap seçeneği kaydedemezsiniz. Lütfen cevabı belirtin veya seçeneği kaldırın.'
    }
  },
  {
    nameEnglish: 'Taiwanese Mandarin Chinese',
    code: 'zh-TW',
    countries: ['Taiwan'],
    translations: {
      submit: '提交',
      continue: '繼續',
      specify: '請指定',
      addAnotherOpenEnded: '新增另一項',
      removeOpenEndedRow: '移除回答',
      deselectAll: '取消全部選擇',

      openAnswerError: '您無法儲存空白的回答選項。請指定回答或取消選擇該選項。'
    }
  },
  {
    nameEnglish: 'Vietnamese',
    code: 'vi',
    countries: ['Vietnam'],
    translations: {
      submit: 'Gửi',
      continue: 'Tiếp tục',
      specify: 'Vui lòng chỉ định',
      addAnotherOpenEnded: 'Thêm câu trả lời khác',
      removeOpenEndedRow: 'Xóa câu trả lời',
      deselectAll: 'Bỏ chọn tất cả',

      openAnswerError: 'Bạn không thể lưu một lựa chọn câu trả lời trống. Vui lòng chỉ định câu trả lời hoặc bỏ chọn tùy chọn.'
    }
  },
  {
    nameEnglish: 'Chinese (Simplified)',
    code: 'zh-Hans',
    countries: ['China', 'Malaysia'],
    translations: {
      submit: '提交',
      continue: '继续',
      specify: '请指定',
      addAnotherOpenEnded: '再添加一项',
      removeOpenEndedRow: '移除回答',
      deselectAll: '全不选',

      openAnswerError: '你不能保存空的回答选项。请指定回答或取消选择该选项。'
    }
  },
  {
    nameEnglish: 'Chinese (Traditional)',
    code: 'zh-Hant',
    countries: ['Hong Kong'],
    translations: {
      submit: '遞交',
      continue: '繼續',
      specify: '請指定',
      addAnotherOpenEnded: '再新增一項',
      removeOpenEndedRow: '移除回答',
      deselectAll: '全不選',

      openAnswerError: '你不能保存空的回答選項。請指定回答或取消選擇該選項。'
    }
  }
];
